/**
 * Hermes Theme - "Mercury's Digital Messenger"
 *
 * Design Philosophy:
 * - Deep blues for trust, communication, and digital sophistication
 * - Contrasting display/editorial typography for distinctive character
 * - Purposeful motion and micro-interactions
 * - Module-specific color coding for wayfinding
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { colors } from './colors';
import { animations } from './animations';

// Extend MUI theme to include custom palette colors
declare module '@mui/material/styles' {
  interface Palette {
    courier: Palette['primary'];
    santa: Palette['primary'];
    email: Palette['primary'];
    user: Palette['primary'];
    editorial: {
      warmWhite: string;
      inkBlack: string;
      paperGray: string;
      accentElectric: string;
    };
  }
  interface PaletteOptions {
    courier?: PaletteOptions['primary'];
    santa?: PaletteOptions['primary'];
    email?: PaletteOptions['primary'];
    user?: PaletteOptions['primary'];
    editorial?: {
      warmWhite?: string;
      inkBlack?: string;
      paperGray?: string;
      accentElectric?: string;
    };
  }
}

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: colors.deepBlue[500],
      light: colors.deepBlue[400],
      dark: colors.deepBlue[600],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: colors.skyBlue[500],
      light: colors.skyBlue[400],
      dark: colors.skyBlue[600],
      contrastText: '#FFFFFF',
    },
    courier: {
      main: colors.courier.main,
      light: colors.courier.light,
      dark: colors.courier.dark,
      contrastText: colors.courier.contrast,
    },
    santa: {
      main: colors.santa.main,
      light: colors.santa.light,
      dark: colors.santa.dark,
      contrastText: colors.santa.contrast,
    },
    email: {
      main: colors.email.main,
      light: colors.email.light,
      dark: colors.email.dark,
      contrastText: colors.email.contrast,
    },
    user: {
      main: colors.user.main,
      light: colors.user.light,
      dark: colors.user.dark,
      contrastText: colors.user.contrast,
    },
    editorial: {
      warmWhite: colors.editorial.warmWhite,
      inkBlack: colors.editorial.inkBlack,
      paperGray: colors.editorial.paperGray,
      accentElectric: colors.editorial.accentElectric,
    },
    background: {
      default: colors.editorial.warmWhite,
      paper: '#FFFFFF',
    },
    text: {
      primary: colors.editorial.inkBlack,
      secondary: '#64748B',
    },
    success: {
      main: colors.success.main,
      light: colors.success.light,
      dark: colors.success.dark,
    },
    error: {
      main: colors.error.main,
      light: colors.error.light,
      dark: colors.error.dark,
    },
    warning: {
      main: colors.warning.main,
      light: colors.warning.light,
      dark: colors.warning.dark,
    },
    info: {
      main: colors.info.main,
      light: colors.info.light,
      dark: colors.info.dark,
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '3rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      fontWeight: 400,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      lineHeight: 1.5,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 4px 8px rgba(15, 23, 42, 0.08)',
    '0 8px 16px rgba(15, 23, 42, 0.10)',
    '0 12px 24px rgba(15, 23, 42, 0.12)',
    '0 16px 32px rgba(15, 23, 42, 0.14)',
    '0 20px 40px rgba(15, 23, 42, 0.16)',
    '0 24px 48px rgba(15, 23, 42, 0.18)',
    '0 28px 56px rgba(15, 23, 42, 0.20)',
    '0 32px 64px rgba(15, 23, 42, 0.22)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.06)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '0.9375rem',
          transition: `all ${animations.duration.normal} ${animations.easing.smooth}`,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.10)',
        },
        elevation3: {
          boxShadow: '0 8px 16px rgba(15, 23, 42, 0.12)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          overflow: 'hidden',
          transition: `transform ${animations.duration.normal} ${animations.easing.smooth}, box-shadow ${animations.duration.normal} ${animations.easing.smooth}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.08em',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${colors.editorial.paperGray}`,
          backgroundImage: 'none',
        },
      },
    },
  },
};

export const hermesTheme = createTheme(themeOptions);
